import { gql, useQuery } from '@apollo/client';
import CapexByYearStackedChartThumbnail from '@assets/images/report-components/capex-by-year-stacked-chart-thumbnail.png';
import { useEffect, useMemo, useState } from 'react';
import { GraphWrapper } from '@/admins/SmartReports/components/Components/Charts/GraphWrapper';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import { useChartColors } from '@/hooks/useChartColors';
import {
  AssetAggregateByEnum,
  AssetAggregatesFieldsEnum,
  type AssetAggregatesStacked,
  AssetStackByEnum,
} from '@/types/GraphqlSchema';
import { LoadingErrorWrapper } from '../Common/LoadingErrorWrapper';
import {
  type ComponentDefinition,
  type ComponentEditProps,
  ComponentViewMode,
  type ComponentViewProps,
  type UseDataType,
} from '../types';
import { getLayout } from './helpers';

const QUERY = gql`
  query assetAggregatesStacked($input: AssetAggregatesInput!) {
    assetAggregatesStacked(input: $input) {
      data {
        type
        group
        capexSum
        values
      }
      stacks
    }
  }
`;

export const COMPONENT_ID = 'capex_by_year_stacked';

const useData = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: UseDataType<AssetAggregatesStacked>) => {
  const [chartDataFetched, setChartDataFetched] = useState(persistedData);

  const { data, loading, error } = useQuery<{ assetAggregatesStacked: AssetAggregatesStacked }>(
    QUERY,
    {
      variables: {
        input: {
          by: AssetAggregateByEnum.CashflowYear,
          fields: [AssetAggregatesFieldsEnum.CapexSum],
          stackBy: AssetStackByEnum.Country,
          portfolio,
        },
      },
      skip: !!persistedData,
    },
  );

  useEffect(() => {
    if (data?.assetAggregatesStacked) {
      const chartData = data.assetAggregatesStacked;
      setChartDataFetched(chartData);
      if (onComponentLoad) {
        onComponentLoad({
          componentId: COMPONENT_ID,
          data: chartData,
        });
      }
    }
  }, [data, onComponentLoad]);

  return { chartData: chartDataFetched, error, loading };
};

const ComponentView = ({
  data,
  mode = ComponentViewMode.View,
}: ComponentViewProps<AssetAggregatesStacked>) => {
  const chartColors = useChartColors();
  const formattedData = useMemo(() => {
    try {
      const { data: chartData, stacks } = data;

      // Create bar traces for each stack (country)
      const barTraces = stacks.map((stackName, idx) => {
        const x = chartData.map(item => item.group); // Time periods (e.g., "Jan 2024")
        const y = chartData.map(item => {
          const stackValues = item.values[stackName];
          return stackValues?.capexSum ?? 0;
        });

        return {
          x,
          y,
          type: 'bar',
          name: stackName,
          stackgroup: stackName,
          marker: {
            color: chartColors[idx % chartColors.length],
          },
        };
      });

      return barTraces;
    } catch {
      return null;
    }
  }, [data, chartColors]);

  if (!formattedData) return null;

  return (
    <ComponentBox>
      <ComponentTitle>Capex By Year (Stacked by Country)</ComponentTitle>
      <GraphWrapper
        data={formattedData}
        layout={getLayout({
          yaxis: { title: { text: 'Capex (US$ million)' } },
          legend: { y: -0.2 },
        })}
        config={{
          staticPlot: mode !== ComponentViewMode.View,
        }}
      />
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<AssetAggregatesStacked>) => {
  const { chartData, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={chartData}>
      {chartData && <ComponentView data={chartData} mode={ComponentViewMode.Edit} />}
    </LoadingErrorWrapper>
  );
};

const ComponentThumbnail = () => (
  <ComponentBox>
    <ComponentTitle sx={{ fontSize: '0.25rem', marginBottom: 0 }}>
      Capex By Year (Stacked by Country)
    </ComponentTitle>
    <img
      src={CapexByYearStackedChartThumbnail}
      alt='Capex By Year Stacked Chart Thumbnail'
      style={{ width: '100%', height: 'auto' }}
    />
  </ComponentBox>
);

export const CapexByYearStackedChartComponent: ComponentDefinition<AssetAggregatesStacked> = {
  id: COMPONENT_ID,
  name: 'Capex By Year',
  type: SmartReportComponentTypesEnum.Chart,
  reportTypes: [SmartReportTypeEnum.Corporate],
  Thumbnail: ComponentThumbnail,
  Component: ComponentEdit,
  ComponentView,
};
